import type { CompositionV2DataQuery } from '@revelio/data-access';

import {
  type BreakdownSingleDataSet,
  DataType,
} from '../../../../DataProvider/types';
import type { CompositionSecondaryDimensionValues } from '../../../../PageConfigProvider/types/dimension';
import { convertBreakdownSingleDataToSheet } from './breakdown-single-data-converter';

interface ShareOfSegmentData {
  compositionData: NonNullable<CompositionV2DataQuery['composition_v2']>;
  secondaryDimension: CompositionSecondaryDimensionValues;
  selectedSegmentIds: string[];
  dateRefs: string[];
}

type SegmentMetric = {
  timeseries: { id: number; count?: number | null; share?: number | null }[];
};

type EntityData = {
  entityName: string;
  firstMetricTimeseriesLength: number;
  selectedMetrics: SegmentMetric[];
};

export const convertShareOfSegmentData = ({
  data,
  primaryDimension,
  secondaryDimension,
  metric,
}: {
  data: ShareOfSegmentData;
  primaryDimension: string;
  secondaryDimension: string;
  metric: string;
}) => {
  const { compositionData, selectedSegmentIds, dateRefs } = data;

  const entityDataByDate = (compositionData.entities ?? []).reduce<
    EntityData[]
  >((acc, entity) => {
    const entityName = entity.metadata?.short_name;
    const metricData = entity.metrics?.[data.secondaryDimension];

    if (!metricData || !entityName) return acc;

    const selectedMetrics = metricData.filter((segment) =>
      selectedSegmentIds.includes(`${segment.metadata.id}`)
    );

    if (selectedMetrics.length === 0) return acc;

    const firstMetricTimeseriesLength = selectedMetrics[0]?.timeseries?.length;
    if (!firstMetricTimeseriesLength || firstMetricTimeseriesLength === 0) {
      return acc;
    }

    return [
      ...acc,
      {
        entityName,
        firstMetricTimeseriesLength,
        selectedMetrics,
      },
    ];
  }, []);

  const dataByDate = entityDataByDate.reduce<
    Record<string, Record<string, { count: number; share: number }>>
  >((dateAcc, { entityName, firstMetricTimeseriesLength, selectedMetrics }) => {
    return Array.from({ length: firstMetricTimeseriesLength }).reduce<
      Record<string, Record<string, { count: number; share: number }>>
    >((timeAcc, _, timePointIndex) => {
      const dateRefId = selectedMetrics[0]?.timeseries?.[timePointIndex]?.id;
      const date = dateRefId ? dateRefs[dateRefId] : undefined;
      if (!date) return timeAcc;

      const { totalShareValue, totalCountValue } = selectedMetrics.reduce<{
        totalShareValue: number;
        totalCountValue: number;
      }>(
        (totals, metric) => {
          const timePoint = metric.timeseries[timePointIndex];
          return timePoint
            ? {
                totalShareValue:
                  totals.totalShareValue + (timePoint.share ?? 0) / 100,
                totalCountValue:
                  totals.totalCountValue + (timePoint.count ?? 0),
              }
            : totals;
        },
        { totalShareValue: 0, totalCountValue: 0 }
      );

      return {
        ...timeAcc,
        [date]: {
          ...(timeAcc[date] || {}),
          [entityName]: {
            count: totalCountValue,
            share: totalShareValue,
          },
        },
      };
    }, dateAcc);
  }, {});

  const sortedEntityNames = [
    ...new Set(entityDataByDate.map((d) => d.entityName)),
  ].sort();
  const sortedDates = Object.keys(dataByDate).sort();

  const breakdownData = sortedEntityNames.map((entityName) => ({
    dim: entityName,
    breakdown: sortedDates.map((date) => ({
      dim: date,
      value: dataByDate[date]?.[entityName]?.count ?? 0,
      share: dataByDate[date]?.[entityName]?.share ?? 0,
    })),
  }));

  const breakdownSingleDataSet: BreakdownSingleDataSet = {
    type: DataType.BREAKDOWN_SINGLE,
    data: breakdownData,
  };

  return convertBreakdownSingleDataToSheet({
    data: breakdownSingleDataSet,
    primaryDimension,
    secondaryDimension,
    metric,
  });
};
